import { z } from 'zod';

const DEAL_TARGET_GROUP = {
  ALL: 'all',
  NEW: 'newCustomers',
  EXISTING: 'returningCustomers',
} as const;

export const dealItemServiceSchema = z.object({
  id: z.number(),
  name: z.string().optional(),
  price: z.number().optional(),
  priceLabel: z.string().optional(),
  originalPrice: z.number().optional(),
  originalPriceLabel: z.string().optional(),
  duration: z.number().optional(),
  durationLabel: z.string().optional(),
  discountPrice: z.number().optional(),
  discountPercent: z.number().optional(),
});

export const dealCroLabelSchema = z.discriminatedUnion('type', [
  z.object({
    type: z.literal('nextAvailableTime'),
    value: z.string(), // e.g Tid idag fr. 15:00
  }),
  z.object({
    type: z.literal('bookingWindow'),
    value: z.string(),
  }),
  z.object({
    type: z.literal('lowStock'),
    value: z.number(),
  }),
  z.object({
    type: z.literal('boughtServices'),
    value: z.number(),
  }),
  z.object({
    type: z.literal('smt'),
  }),
  z.object({
    type: z.literal('productDiscount'),
  }),
]);

const dealCroLabelsSchema = z.array(dealCroLabelSchema);

export const brandTemplateSchema = z.object({
  id: z.number(),
  name: z.string(),
  slug: z.string(),
  logoImageUrl: z.string().optional(),
  logoImageUrlSecondary: z.string().optional(),
  backgroundColor: z.string().optional(), // <- hex color value
});

const brandDealDetailsSchema = z.object({
  id: z.number(),
  template: brandTemplateSchema,
  productImageUrl: z.string().optional(),
  mainImageUrl: z.string().optional(),
  bullets: z.array(z.string()).optional(),
  readMoreUrl: z.string().optional(),
  priceLabel: z.string().optional(),
  originalPriceLabel: z.string().optional(),
  description: z.string().optional(),
  title: z.string(),
});

const productSchema = z.object({
  name: z.string(),
  price: z.number(),
  priceLabel: z.string(),
  originalPrice: z.number().optional(),
  originalPriceLabel: z.string().optional(),
});

const discountLabelSchema = z.object({
  type: z.enum(['service', 'product']),
  label: z.string(),
});

export const dealItemBaseSchema = z.object({
  id: z.number(),
  startDate: z.number(),
  endDate: z.number(),
  name: z.string().optional(),
  campaignId: z.string().optional(),
  status: z.number().optional(),
  createdAt: z.string().optional(),
  croLabels: dealCroLabelsSchema.optional(),
  description: z.string().optional(),
  conditions: z.string().optional(),
  imageUrl: z.string().optional(),
  publicationStart: z.number().optional(),
  publicationEnd: z.number().optional(),
  maxCount: z.number().optional(),
  maxCountCustomer: z.number().optional(),
  targetGroup: z.nativeEnum(DEAL_TARGET_GROUP).optional(),
  price: z.number().optional(),
  priceLabel: z.string().optional(),
  originalPrice: z.number().optional(),
  originalPriceLabel: z.string().optional(),
  durationLabel: z.string().optional(),
  discountLabels: z
    .array(discountLabelSchema.nullable().catch(null))
    .transform((arr) => arr.filter(Boolean))
    .optional(),
  discountLabel: z.string().optional(),
  services: z.array(dealItemServiceSchema),
  products: z.array(productSchema).optional(),
  shareUrl: z.string().optional(),
  brand: brandDealDetailsSchema.optional(),
});

export type DealCroLabel = z.infer<typeof dealCroLabelSchema>;
export type BrandTemplate = z.infer<typeof brandTemplateSchema>;
export type BrandDealDetails = z.infer<typeof brandDealDetailsSchema>;
export type DealItemBase = z.infer<typeof dealItemBaseSchema>;
